import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Grid, TextField, Button, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Paper, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Alert,
  FormControl, InputLabel, Select, MenuItem, InputAdornment,
  TablePagination, Box, Chip, Card, CardContent, Autocomplete
} from '@mui/material';
import {
  Edit, Delete, Search, Add, Receipt, AttachMoney,
  Description, AccountBalance, Info
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';

export default function Retenciones() {
  const { getTenantKey } = useContext(AuthContext);
  const [retenciones, setRetenciones] = useState([]);
  const [retencionesFiltradas, setRetencionesFiltradas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  
  const [form, setForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    cliente: null,
    numeroComprobante: '',
    autorizacion: '',
    baseImponible: '',
    codigoRetencion: '303',
    porcentajeRetencion: '10',
    valorRetenido: '',
    tipo: 'renta',
    observaciones: '',
    esRimpe: false,
    esAgenteRetencion: false
  });
  
  const [editIndex, setEditIndex] = useState(-1);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Códigos de retención RENTA según Resolución NAC-DGERCGC26-00000009 (vigente 13 marzo 2026)
  const codigosRenta = [
    { codigo: '303', descripcion: 'Honorarios Profesionales (Personas Naturales - Intelecto)', porcentaje: 10 },
    { codigo: '304', descripcion: 'Servicios con predominio del Intelecto (Sociedades)', porcentaje: 5 },
    { codigo: '307', descripcion: 'Servicios con predominio de la Mano de Obra', porcentaje: 3 },
    { codigo: '308', descripcion: 'Utilización o aprovechamiento de Imagen (Influencers/Artistas)', porcentaje: 10 },
    { codigo: '309', descripcion: 'Servicios de Transporte Público y Privado (Pasajeros/Carga)', porcentaje: 1 },
    { codigo: '310', descripcion: 'Servicios de Construcción (Urbanización/Lotización)', porcentaje: 2 },
    { codigo: '311', descripcion: 'Arrendamiento de Inmuebles (Locales comerciales/Oficinas)', porcentaje: 10 },
    { codigo: '312', descripción: 'Transferencia de Bienes Muebles de naturaleza corporal', porcentaje: 2 },
    { codigo: '319', descripcion: 'Seguros y Reaseguros (Primas y Cesiones)', porcentaje: 2 },
    { codigo: '320', descripcion: 'Comisiones por intermediación (Agencias, Corredores)', porcentaje: 10 },
    { codigo: '322', descripcion: 'Rendimientos Financieros (Intereses pagados)', porcentaje: 2 },
    { codigo: '332', descripcion: 'Otras compras de bienes y servicios no sujetos a retención', porcentaje: 0 },
    { codigo: '343', descripcion: 'Pagos a Contribuyentes Régimen RIMPE Emprendedor', porcentaje: 1 },
    { codigo: '344', descripcion: 'Otras Retenciones Aplicables (Tarifa General Residual)', porcentaje: 3 },
    { codigo: '346', descripcion: 'Pagos por Publicidad y Propaganda', porcentaje: 3 },
  ];

  // Códigos de retención IVA según Resolución NAC-DGERCGC26-00000009
  const codigosIva = [
    { codigo: '1', descripcion: 'Retención de IVA en Bienes (Sociedad a Sociedad)', porcentaje: 30 },
    { codigo: '2', descripcion: 'Retención de IVA en Servicios (Sociedad a Sociedad)', porcentaje: 70 },
    { codigo: '3', descripcion: 'Retención de IVA en Honorarios Profesionales / Arriendo', porcentaje: 100 },
    { codigo: '9', descripcion: 'Agente Retención / Especial a Especial (Bienes)', porcentaje: 10 },
    { codigo: '10', descripcion: 'Agente Retención / Especial a Especial (Servicios)', porcentaje: 20 },
  ];

  useEffect(() => {
    const dataRetenciones = JSON.parse(localStorage.getItem(getTenantKey('retenciones'))) || [];
    setRetenciones(dataRetenciones);
    setRetencionesFiltradas(dataRetenciones);

    const dataClientes = JSON.parse(localStorage.getItem(getTenantKey('clientes'))) || [];
    setClientes(dataClientes.filter(c => c.tipo === 'cliente'));
  }, [getTenantKey]);

  // Búsqueda
  useEffect(() => {
    if (busqueda.trim()) {
      const termino = busqueda.toLowerCase();
      const resultado = retenciones.filter(r =>
        r.cliente?.nombre.toLowerCase().includes(termino) ||
        r.numeroComprobante.includes(termino) ||
        r.autorizacion.includes(termino)
      );
      setRetencionesFiltradas(resultado);
    } else {
      setRetencionesFiltradas(retenciones);
    }
    setPage(0);
  }, [busqueda, retenciones]);

  // Calcular valor retenido automáticamente
  useEffect(() => {
    const base = parseFloat(form.baseImponible) || 0;
    const porcentaje = parseFloat(form.porcentajeRetencion) || 0;
    
    // Si es RIMPE Negocio Popular, no se retiene (excepto liquidación de compra)
    if (form.esRimpe && form.tipo === 'renta') {
      setForm(prev => ({
        ...prev,
        valorRetenido: '0.00'
      }));
      return;
    }
    
    const valorRetenido = (base * porcentaje / 100).toFixed(2);
    
    setForm(prev => ({
      ...prev,
      valorRetenido
    }));
  }, [form.baseImponible, form.porcentajeRetencion, form.esRimpe, form.tipo]);

  // Actualizar porcentaje cuando cambia el código
  useEffect(() => {
    const codigos = form.tipo === 'renta' ? codigosRenta : codigosIva;
    const codigo = codigos.find(c => c.codigo === form.codigoRetencion);
    if (codigo) {
      setForm(prev => ({
        ...prev,
        porcentajeRetencion: codigo.porcentaje.toString()
      }));
    }
  }, [form.codigoRetencion, form.tipo]);

  const validateForm = () => {
    if (!form.cliente) {
      showSnackbar('Debe seleccionar un cliente', 'error');
      return false;
    }
    if (!form.numeroComprobante.trim()) {
      showSnackbar('El número de comprobante es obligatorio', 'error');
      return false;
    }
    if (!form.autorizacion.trim()) {
      showSnackbar('El número de autorización es obligatorio', 'error');
      return false;
    }
    const base = parseFloat(form.baseImponible);
    if (isNaN(base) || base <= 0) {
      showSnackbar('La base imponible debe ser mayor a cero', 'error');
      return false;
    }
    return true;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const newRetencion = {
      ...form,
      id: editIndex >= 0 ? retenciones[editIndex].id : Date.now().toString(),
      fechaRegistro: new Date().toISOString()
    };

    let newRetenciones;
    if (editIndex >= 0) {
      newRetenciones = [...retenciones];
      newRetenciones[editIndex] = newRetencion;
      showSnackbar('Retención actualizada');
    } else {
      newRetenciones = [...retenciones, newRetencion];
      showSnackbar('Retención registrada');
    }

    setRetenciones(newRetenciones);
    localStorage.setItem(getTenantKey('retenciones'), JSON.stringify(newRetenciones));
    resetForm();
  };

  const resetForm = () => {
    setForm({
      fecha: new Date().toISOString().split('T')[0],
      cliente: null,
      numeroComprobante: '',
      autorizacion: '',
      baseImponible: '',
      codigoRetencion: '303',
      porcentajeRetencion: '10',
      valorRetenido: '',
      tipo: 'renta',
      observaciones: '',
      esRimpe: false,
      esAgenteRetencion: false
    });
    setEditIndex(-1);
    setOpenDialog(false);
  };

  const handleEdit = (index) => {
    setForm(retencionesFiltradas[index]);
    setEditIndex(retenciones.findIndex(r => r.id === retencionesFiltradas[index].id));
    setOpenDialog(true);
  };

  const handleDelete = (index) => {
    setDeleteIndex(retenciones.findIndex(r => r.id === retencionesFiltradas[index].id));
    setOpenDelete(true);
  };

  const confirmDelete = () => {
    const newRetenciones = retenciones.filter((_, i) => i !== deleteIndex);
    setRetenciones(newRetenciones);
    localStorage.setItem(getTenantKey('retenciones'), JSON.stringify(newRetenciones));
    showSnackbar('Retención eliminada');
    setOpenDelete(false);
  };

  // Estadísticas
  const stats = {
    total: retenciones.length,
    totalRetenido: retenciones.reduce((sum, r) => sum + parseFloat(r.valorRetenido || 0), 0),
    renta: retenciones.filter(r => r.tipo === 'renta').length,
    iva: retenciones.filter(r => r.tipo === 'iva').length,
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header con estadísticas */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>
            Gestión de Retenciones
          </Typography>
          <Chip
            icon={<Info />}
            label="Resolución NAC-DGERCGC26-00000009 (Vigente 13/03/2026)"
            color="primary"
            variant="outlined"
          />
        </Box>
        
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd', boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt sx={{ color: '#1976d2', fontSize: 32 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Retenciones</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {stats.total}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e8f5e9', boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney sx={{ color: '#388e3c', fontSize: 32 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Retenido</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#388e3c' }}>
                      ${stats.totalRetenido.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fff3e0', boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalance sx={{ color: '#f57c00', fontSize: 32 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Ret. Renta</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                      {stats.renta}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#f3e5f5', boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Description sx={{ color: '#7b1fa2', fontSize: 32 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Ret. IVA</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                      {stats.iva}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Barra de acciones */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por cliente, número de comprobante o autorización..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
              sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' } }}
            >
              Nueva Retención
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Fecha</strong></TableCell>
              <TableCell><strong>Cliente</strong></TableCell>
              <TableCell><strong>Tipo</strong></TableCell>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Descripción</strong></TableCell>
              <TableCell align="right"><strong>Base Imponible</strong></TableCell>
              <TableCell align="center"><strong>%</strong></TableCell>
              <TableCell align="right"><strong>Valor Retenido</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {retencionesFiltradas.length > 0 ? (
              retencionesFiltradas
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((retencion, idx) => {
                  const codigos = retencion.tipo === 'renta' ? codigosRenta : codigosIva;
                  const codigoInfo = codigos.find(c => c.codigo === retencion.codigoRetencion);
                  
                  return (
                    <TableRow key={retencion.id} hover>
                      <TableCell>{new Date(retencion.fecha).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {retencion.cliente?.nombre || '-'}
                        {retencion.esRimpe && (
                          <Chip label="RIMPE" size="small" color="warning" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={retencion.tipo === 'renta' ? 'Renta' : 'IVA'}
                          size="small"
                          color={retencion.tipo === 'renta' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {retencion.codigoRetencion}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {codigoInfo?.descripcion || '-'}
                      </TableCell>
                      <TableCell align="right">
                        ${parseFloat(retencion.baseImponible || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${retencion.porcentajeRetencion}%`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                        ${parseFloat(retencion.valorRetenido || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(idx)}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(idx)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {busqueda ? 'No se encontraron resultados' : 'No hay retenciones registradas'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={retencionesFiltradas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Filas por página:"
        />
      </TableContainer>

      {/* Dialog Crear/Editar */}
      <Dialog open={openDialog} onClose={resetForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: '#fff' }}>
          {editIndex >= 0 ? 'Editar Retención' : 'Nueva Retención'}
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Fecha *"
                type="date"
                value={form.fecha}
                onChange={e => setForm({ ...form, fecha: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Retención</InputLabel>
                <Select
                  value={form.tipo}
                  label="Tipo de Retención"
                  onChange={e => setForm({ ...form, tipo: e.target.value, codigoRetencion: e.target.value === 'renta' ? '303' : '1' })}
                >
                  <MenuItem value="renta">Retención en la Fuente (Renta)</MenuItem>
                  <MenuItem value="iva">Retención de IVA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Es RIMPE</InputLabel>
                  <Select
                    value={form.esRimpe ? 'si' : 'no'}
                    label="Es RIMPE"
                    onChange={e => setForm({ ...form, esRimpe: e.target.value === 'si' })}
                  >
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="si">Sí</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                size="small"
                options={clientes}
                getOptionLabel={(option) => `${option.nombre} - ${option.ruc}`}
                value={form.cliente}
                onChange={(_, newValue) => setForm({ ...form, cliente: newValue })}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente *" required />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Código de Retención</InputLabel>
                <Select
                  value={form.codigoRetencion}
                  label="Código de Retención"
                  onChange={e => setForm({ ...form, codigoRetencion: e.target.value })}
                >
                  {(form.tipo === 'renta' ? codigosRenta : codigosIva).map(c => (
                    <MenuItem key={c.codigo} value={c.codigo}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {c.codigo} - {c.porcentaje}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.descripcion}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {form.esRimpe && form.tipo === 'renta' && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<Info />}>
                  <strong>RIMPE Negocio Popular:</strong> No se aplica retención (0%) según normativa vigente.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Número de Comprobante *"
                value={form.numeroComprobante}
                onChange={e => setForm({ ...form, numeroComprobante: e.target.value })}
                placeholder="001-001-000000001"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Número de Autorización *"
                value={form.autorizacion}
                onChange={e => setForm({ ...form, autorizacion: e.target.value })}
                placeholder="49 dígitos"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Base Imponible *"
                type="number"
                value={form.baseImponible}
                onChange={e => setForm({ ...form, baseImponible: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Porcentaje"
                type="number"
                value={form.porcentajeRetencion}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  readOnly: true,
                }}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Valor Retenido"
                type="number"
                value={form.valorRetenido}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  readOnly: true,
                }}
                disabled
                sx={{
                  '& .MuiInputBase-input': { fontWeight: 700, fontSize: '1.1rem', color: '#d32f2f' }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Observaciones"
                multiline
                rows={2}
                value={form.observaciones}
                onChange={e => setForm({ ...form, observaciones: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetForm}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar esta retención? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
